import type {
  AIProviderConfigDto,
  AIProviderCreate,
  AIProviderUpdate,
  AuditPage,
  AuditQuery,
  CambioDePassword,
  CorreoAjustesDto,
  CorreoAjustesUpdate,
  CorridaDto,
  CorridaPage,
  CorridaQuery,
  Inicio,
  LoginInput,
  ModeloDisponible,
  PrestamoUpdate,
  RegistroFilaDto,
  RegistroImport,
  ResetPasswordInput,
  SesionAbierta,
  SesionActual,
  SesionIaDto,
  SistemaDto,
  TareaCreate,
  TareaDto,
  TareaUpdate,
  UsageDto,
  UserConPassword,
  UserCreate,
  UserDto,
  UserUpdate,
  VistaPrevia,
} from "@chuy/types";

/**
 * La capa de transporte, delgadita.
 *
 * El tablero no guarda reglas de negocio: le habla a la API, pinta lo que le
 * regresan y avisa si algo truena. Todo lo que decide *qué debe pasar* vive en
 * el servidor.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * El token anti-CSRF, leído de su cookie.
 *
 * El servidor pone dos cookies al entrar: la de la sesión, que el JavaScript no
 * puede leer (`httpOnly`), y ésta, que sí. El truco está justo en esa asimetría:
 * un sitio ajeno puede hacer que tu navegador *mande* la cookie de sesión, pero
 * no puede *leer* ésta para copiar su valor al encabezado, porque se lo impide la
 * política de mismo origen. Sin las dos juntas, el servidor no acepta nada que
 * escriba.
 */
function tokenCsrf(): string {
  const encontrada = document.cookie
    .split(";")
    .map((parte) => parte.trim())
    .find((parte) => parte.startsWith("chuy_csrf="));
  return encontrada ? decodeURIComponent(encontrada.slice("chuy_csrf=".length)) : "";
}

/**
 * Lo que se hace cuando el servidor contesta que ya no hay sesión.
 *
 * Lo pone `useSesion` al arrancar. Vive como una variable suelta y no como un
 * import para que esta capa siga sin saber nada del router ni de las pantallas:
 * su chamba es hablar HTTP, no decidir a dónde mandar a nadie.
 */
let alPerderLaSesion: (() => void) | null = null;

export function cuandoSePierdaLaSesion(accion: () => void): void {
  alPerderLaSesion = accion;
}

/**
 * Lo que se hace cuando el proveedor contesta que se acabó la cuota.
 *
 * Mismo truco que arriba, y por la misma razón. Se avisa desde aquí —y no desde
 * cada pantalla que le habla a la API— porque el límite no es problema de la
 * pantalla donde te agarró: mientras dure, ninguna tarea va a correr. Cada pantalla sigue enseñando su propio aviso de pasadita;
 * esto es lo que además prende el letrero de arriba, que no se va hasta que se
 * repone.
 */
let alTocarElLimite: ((detalle: { mensaje: string; reponeEn: string | null }) => void) | null = null;

export function cuandoSeAcabeElCredito(
  accion: (detalle: { mensaje: string; reponeEn: string | null }) => void,
): void {
  alTocarElLimite = accion;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const escribe = method !== "GET" && method !== "HEAD";

  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      // Sin esto el navegador no manda la cookie de sesión y nada de esto
      // funciona en desarrollo, donde Vite y la API viven en puertos distintos.
      credentials: "same-origin",
      headers: {
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...(escribe ? { "X-Chuy-CSRF": tokenCsrf() } : {}),
        ...init.headers,
      },
    });
  } catch (error) {
    throw new ApiError(
      "No alcanzo al servidor. ¿Seguro que Chuy anda arriba en el puerto 3001?",
      0,
      "NETWORK_ERROR",
      error,
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? safeJson(text) : null;

  if (!response.ok) {
    const body = payload as { error?: { message?: string; code?: string; details?: unknown } };

    // Se venció la sesión, o la cerraron desde otro lado. Se avisa una vez y la
    // app se va sola a la pantalla de entrar, en vez de dejar al tablero
    // enseñando errores rojos en cada recuadro.
    if (response.status === 401 && !path.startsWith("/api/auth/")) {
      alPerderLaSesion?.();
    }

    if (body?.error?.code === "LIMITE_DE_USO") {
      const detalles = body.error.details as { reponeEn?: string | null } | undefined;
      alTocarElLimite?.({
        mensaje: body.error.message ?? "Se acabó el crédito por ahorita.",
        reponeEn: detalles?.reponeEn ?? null,
      });
    }

    throw new ApiError(
      body?.error?.message ?? `La petición no pasó (código ${response.status})`,
      response.status,
      body?.error?.code ?? "UNKNOWN",
      body?.error?.details,
    );
  }

  return payload as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });

/** Arma "?a=1&b=2" saltándose lo que venga vacío. */
function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  const suffix = search.toString();
  return suffix ? `?${suffix}` : "";
}

export const api = {
  /** La puerta: entrar, salir, saber quién eres, cambiarte la contraseña. */
  auth: {
    entrar: (input: LoginInput) =>
      request<SesionActual>("/api/auth/entrar", { method: "POST", ...json(input) }),
    yo: () => request<SesionActual>("/api/auth/yo"),
    salir: () => request<{ ok: true }>("/api/auth/salir", { method: "POST" }),
    cambiarPassword: (input: CambioDePassword) =>
      request<SesionActual>("/api/auth/password", { method: "POST", ...json(input) }),
    sesiones: () =>
      request<{ items: SesionAbierta[] }>("/api/auth/sesiones").then((r) => r.items),
    cerrarLasDemas: () =>
      request<{ cerradas: number }>("/api/auth/sesiones/cerrar-las-demas", { method: "POST" }),
  },

  /** El panel del administrador. Un invitado recibe 403 en todo esto. */
  admin: {
    usuarios: () => request<{ items: UserDto[] }>("/api/admin/usuarios").then((r) => r.items),
    usuario: (id: string) => request<UserDto>(`/api/admin/usuarios/${id}`),
    crear: (input: UserCreate) =>
      request<UserConPassword>("/api/admin/usuarios", { method: "POST", ...json(input) }),
    editar: (id: string, patch: UserUpdate) =>
      request<UserDto>(`/api/admin/usuarios/${id}`, { method: "PATCH", ...json(patch) }),
    resetearPassword: (id: string, input: ResetPasswordInput = {}) =>
      request<UserConPassword>(`/api/admin/usuarios/${id}/password`, {
        method: "POST",
        ...json(input),
      }),
    borrar: (id: string) =>
      request<void>(`/api/admin/usuarios/${id}`, { method: "DELETE" }),
    prestarCredencial: (id: string, input: PrestamoUpdate) =>
      request<UserDto>(`/api/admin/usuarios/${id}/credencial`, { method: "PUT", ...json(input) }),
    /** Cuánto disco queda en el servidor y qué se lo está comiendo. */
    disco: () =>
      request<{
        total: number;
        libre: number;
        usadoPorChuy: number;
        desglose: Array<{ carpeta: string; bytes: number }>;
        utilizacion: number;
      }>("/api/admin/disco"),
    bitacora: (query: Partial<AuditQuery> = {}) =>
      request<AuditPage>(`/api/admin/bitacora${qs(query)}`),
    consumo: () =>
      request<{
        items: Array<{
          userId: string;
          username: string;
          name: string;
          usaCredencialPrestada: boolean;
          runs: number;
          costUsd: number;
          inputTokens: number;
          outputTokens: number;
        }>;
        generadoEn: string;
      }>("/api/admin/consumo"),
  },

  /** El resumen de la portada. */
  inicio: () => request<Inicio>("/api/inicio"),

  tareas: {
    list: () => request<{ items: TareaDto[] }>("/api/tareas").then((r) => r.items),
    get: (id: string) => request<TareaDto>(`/api/tareas/${id}`),
    create: (input: Partial<TareaCreate> & Pick<TareaCreate, "nombre" | "instrucciones">) =>
      request<TareaDto>("/api/tareas", { method: "POST", ...json(input) }),
    update: (id: string, patch: TareaUpdate) =>
      request<TareaDto>(`/api/tareas/${id}`, { method: "PATCH", ...json(patch) }),
    duplicar: (id: string) => request<TareaDto>(`/api/tareas/${id}/duplicar`, { method: "POST" }),
    remove: (id: string) => request<void>(`/api/tareas/${id}`, { method: "DELETE" }),
    /** Correr ahorita. Regresa en cuanto arranca: la corrida se sigue por su id. */
    correr: (id: string) => request<{ corridaId: string }>(`/api/tareas/${id}/correr`, { method: "POST" }),
    cancelar: (id: string) => request<{ cancelada: boolean }>(`/api/tareas/${id}/cancelar`, { method: "POST" }),
    vistaPrevia: (id: string) => request<VistaPrevia>(`/api/tareas/${id}/vista-previa`),
  },

  registro: {
    list: (tareaId: string) =>
      request<{ items: RegistroFilaDto[] }>(`/api/tareas/${tareaId}/registro`).then((r) => r.items),
    agregar: (tareaId: string, datos: Record<string, string>) =>
      request<RegistroFilaDto>(`/api/tareas/${tareaId}/registro`, { method: "POST", ...json({ datos }) }),
    editar: (tareaId: string, filaId: string, datos: Record<string, string>) =>
      request<RegistroFilaDto>(`/api/tareas/${tareaId}/registro/${filaId}`, { method: "PATCH", ...json({ datos }) }),
    borrar: (tareaId: string, filaId: string) =>
      request<void>(`/api/tareas/${tareaId}/registro/${filaId}`, { method: "DELETE" }),
    vaciar: (tareaId: string) =>
      request<{ borradas: number }>(`/api/tareas/${tareaId}/registro`, { method: "DELETE" }),
    importar: (tareaId: string, input: RegistroImport) =>
      request<{ agregadas: number }>(`/api/tareas/${tareaId}/registro/importar`, { method: "POST", ...json(input) }),
    /** La liga de descarga: el navegador la baja con la cookie de sesión. */
    csvUrl: (tareaId: string) => `/api/tareas/${tareaId}/registro.csv`,
  },

  corridas: {
    list: (query: Partial<CorridaQuery> = {}) => request<CorridaPage>(`/api/corridas${qs(query)}`),
    get: (id: string) => request<CorridaDto>(`/api/corridas/${id}`),
    reenviar: (id: string) => request<CorridaDto>(`/api/corridas/${id}/reenviar`, { method: "POST" }),
    /** El resultado ya envuelto en la plantilla del correo. */
    correo: (id: string) => request<{ html: string }>(`/api/corridas/${id}/correo`).then((r) => r.html),
    remove: (id: string) => request<void>(`/api/corridas/${id}`, { method: "DELETE" }),
  },

  correo: {
    get: () => request<CorreoAjustesDto>("/api/correo"),
    update: (patch: CorreoAjustesUpdate) =>
      request<CorreoAjustesDto>("/api/correo", { method: "PATCH", ...json(patch) }),
    probar: (para?: string) =>
      request<{ enviadoA: string[] }>("/api/correo/probar", { method: "POST", ...json(para ? { para } : {}) }),
  },

  providers: {
    list: () => request<{ items: AIProviderConfigDto[] }>("/api/providers").then((r) => r.items),
    create: (input: AIProviderCreate) =>
      request<AIProviderConfigDto>("/api/providers", { method: "POST", ...json(input) }),
    update: (id: string, patch: AIProviderUpdate) =>
      request<AIProviderConfigDto>(`/api/providers/${id}`, { method: "PATCH", ...json(patch) }),
    remove: (id: string) => request<void>(`/api/providers/${id}`, { method: "DELETE" }),
    activate: (id: string) =>
      request<AIProviderConfigDto>(`/api/providers/${id}/activate`, { method: "POST" }),
    test: (id: string) =>
      request<{ ok: boolean; detail: string; remedio?: string }>(`/api/providers/${id}/test`, {
        method: "POST",
      }),
    /** Los modelos que la cuenta puede usar, preguntándole al servicio. */
    modelos: (id: string) =>
      request<{ modelos: ModeloDisponible[]; delServicio: boolean }>(`/api/providers/${encodeURIComponent(id)}/modelos`),
  },

  usage: (force = false) => request<UsageDto>(`/api/usage${qs({ force: force || undefined })}`),

  horario: (horario: string, zonaHoraria?: string) =>
    request<{ valido: boolean; legible: string; zonaHoraria: string; proximas: string[] }>(
      "/api/horario/vista-previa",
      { method: "POST", ...json({ horario, zonaHoraria }) },
    ),

  sistema: () => request<SistemaDto>("/api/sistema"),

  system: {
    /** El pulso de adentro: cuántas corridas andan vivas y cómo anda la credencial. */
    pulso: () =>
      request<{ ok: boolean; version: string; corriendo: number; ia?: { ok: boolean; detail: string; remedio?: string } }>(
        "/api/system/pulso",
      ),
    /** La credencial de la IA: cómo anda y qué se puede hacer para componerla. */
    ia: () => request<SesionIaDto>("/api/system/ia"),
    /** Vuelve a comprobar de verdad (el "ya me reconecté"). */
    revisarIa: () => request<SesionIaDto>("/api/system/ia/revisar", { method: "POST" }),
    guardarCredencial: (secreto: string) =>
      request<SesionIaDto>("/api/system/ia/credencial", { method: "POST", ...json({ secreto }) }),
    quitarCredencial: () => request<SesionIaDto>("/api/system/ia/credencial", { method: "DELETE" }),
    /** Abre una terminal de esta Mac con el comando ya escrito. */
    abrirTerminal: (comando: "login" | "token") =>
      request<{ ok: true }>("/api/system/ia/terminal", { method: "POST", ...json({ comando }) }),
    barrido: () => request<{ recovered: number; skipped: number }>("/api/capataz/barrido", { method: "POST" }),
  },
};
